import React, { useEffect, useState } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import EmptyPage from "../../common/emptyPage";
import TooltipText from "../../common/tooltipText";
import BlogCreateForm from "./BlogCreateForm";
import {
  getBlogs,
  getBlog,
  createBlog,
  updateBlog,
  deleteBlog,
} from "../../../helper/apicalls/blog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import "../../../styles/admin/blog.scss";
import Cookies from "js-cookie";

const BlogPage = () => {
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const role = Cookies.get("role");
  const portalBase =
    role === "admin"
      ? "/admin"
      : role === "teacher"
        ? "/teacher"
        : "/institution";
  const [searchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [isConfirmPublishOpen, setIsConfirmPublishOpen] = useState(false);
  const [selectedForPublish, setSelectedForPublish] = useState(null);
  const [isCreateInline, setIsCreateInline] = useState(false);

  useEffect(() => {
    setHeaderName("Current Affairs");
    setIsShowSearch(false);
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const courseKey = (searchParams.get("course") || "").trim();
        const data = await getBlogs(courseKey);
        setBlogs(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("Failed to load blogs. Please try again.");
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [searchParams]);

  const handleCreateClick = () => {
    setEditingBlog(null);
    setIsCreateInline(true);
  };

  const handleEditClick = async (blog) => {
    const id = blog?.id || blog?._id;
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const fullBlog = await getBlog(id);

      setEditingBlog(fullBlog || blog || null);
      setIsCreateInline(true);
      setOpenMenuIndex(null);
    } catch (err) {
      setError("Failed to load blog details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPublish = (blog) => {
    const id = blog?.id || blog?._id;
    if (!id) return;
    setSelectedForPublish(id);
    setIsConfirmPublishOpen(true);
    setOpenMenuIndex(null);
  };

  const handleConfirmPublish = async () => {
    if (!selectedForPublish) {
      setIsConfirmPublishOpen(false);
      return;
    }
    try {
      setLoading(true);
      await updateBlog(selectedForPublish, { publish: true });
      setIsConfirmPublishOpen(false);
      setSelectedForPublish(null);
      const courseKey = (searchParams.get("course") || "").trim();
      const data = await getBlogs(courseKey);
      setBlogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to publish blog. Please try again.");
      setIsConfirmPublishOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDelete = (blog) => {
    const id = blog?.id || blog?._id;
    if (!id) return;
    setSelectedForDelete(id);
    setIsConfirmDeleteOpen(true);
    setOpenMenuIndex(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) {
      setIsConfirmDeleteOpen(false);
      return;
    }
    try {
      setLoading(true);
      await deleteBlog(selectedForDelete);
      setIsConfirmDeleteOpen(false);
      setSelectedForDelete(null);
      const courseKey = (searchParams.get("course") || "").trim();
      const data = await getBlogs(courseKey);
      setBlogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to delete blog. Please try again.");
      setIsConfirmDeleteOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInlineSave = async (payload) => {
    try {
      setLoading(true);
      setError(null);
      const courseKey = (searchParams.get("course") || "").trim();
      const blogId = editingBlog?.id || editingBlog?._id;

      if (blogId) {
        await updateBlog(blogId, payload);
      } else {
        await createBlog(payload, courseKey);
      }

      setIsCreateInline(false);
      setEditingBlog(null);

      const data = await getBlogs(courseKey);
      setBlogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to save blog. Please try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    setOpenMenuIndex((prev) => (prev === index ? null : index));
  };

  const closeMenu = () => setOpenMenuIndex(null);

  useEffect(() => {
    if (openMenuIndex === null) return;

    const handleDocumentClick = (event) => {
      const target = event.target;
      if (target.closest && target.closest(".overall-tests-menu")) {
        return;
      }
      setOpenMenuIndex(null);
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [openMenuIndex]);

  const handleCardClick = (blog) => {
    const id = blog?.id || blog?._id;
    if (!id) return;
    navigate(`${portalBase}/blog/view?id=${encodeURIComponent(id)}`);
  };

  const isBlogPublished = (blog) => {
    if (!blog) return false;
    if (typeof blog.publish === "boolean") return blog.publish;
    if (typeof blog.published === "boolean") return blog.published;
    if (typeof blog.is_published === "boolean") return blog.is_published;
    if (typeof blog.status === "string") {
      return blog.status.toLowerCase() === "published";
    }
    return false;
  };

  return (
    <div className="blog-container">
      {loading && <GlobalLoader visible={loading} />}
      <div className="blog-header-actions">
        {!isCreateInline && (
          <button
            type="button"
            className="overall-tests-add-btn"
            onClick={handleCreateClick}
            disabled={loading}
          >
            <AddCircleOutlineOutlinedIcon className="videos-add-icon" />
            <span>Create Blog</span>
          </button>
        )}
      </div>
      {error && isCreateInline && (
        <div className="overall-tests-error">{error}</div>
      )}
      {isCreateInline && (
        <BlogCreateForm
          submitting={loading}
          onSave={handleInlineSave}
          initialData={editingBlog}
          onCancel={() => {
            setIsCreateInline(false);
            setEditingBlog(null);
          }}
        />
      )}
      {!isCreateInline && (
        <>
          {!loading && error ? (
            <EmptyPage content={error} />
          ) : !loading && !error && blogs?.length === 0 ? (
            <EmptyPage content={"No blogs found."} />
          ) : (
            <div className="blog-grid">
              {blogs.map((blog, index) => (
                <div
                  key={blog.id || blog._id || blog.title}
                  className="blog-card"
                  onClick={() => handleCardClick(blog)}
                >
                  <div className="blog-card-body">
                    <div
                      className={`blog-card-status ${
                        isBlogPublished(blog)
                          ? "blog-card-status--published"
                          : "blog-card-status--draft"
                      }`}
                    >
                      {isBlogPublished(blog) ? "Published" : "Draft"}
                    </div>
                    <div
                      className="overall-tests-menu"
                      style={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <button
                        type="button"
                        className="overall-tests-menu-button overall-tests-menu-button--blog"
                        onClick={(e) => toggleMenu(e, index)}
                      >
                        <MoreVertIcon className="overall-tests-menu-icon" />
                      </button>
                      {openMenuIndex === index && (
                        <div
                          className="overall-tests-menu-list"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="overall-tests-menu-item"
                            onClick={() => handleEditClick(blog)}
                          >
                            Edit
                          </button>
                          {!isBlogPublished(blog) && (
                            <button
                              type="button"
                              className="overall-tests-menu-item"
                              onClick={() => handleRequestPublish(blog)}
                            >
                              Publish
                            </button>
                          )}
                          <button
                            type="button"
                            className="overall-tests-menu-item overall-tests-menu-item--danger"
                            onClick={() => handleRequestDelete(blog)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {blog?.image_url && (
                      <div className="blog-card-image-wrapper">
                        <img
                          src={blog.image_url}
                          alt={blog.title || "Blog thumbnail"}
                          className="blog-card-image"
                        />
                      </div>
                    )}
                    <h3>
                      <TooltipText
                        text={blog?.title || blog?.name || "Untitled Blog"}
                        className="blog-card-title"
                        multiline
                      />
                    </h3>
                    {(blog?.blog_description || blog?.description) &&
                      (() => {
                        const descriptionText =
                          blog.blog_description || blog.description;
                        return (
                          <TooltipText
                            text={descriptionText}
                            className="blog-card-description"
                            multiline
                          />
                        );
                      })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <DeleteConfirmPopUp
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        handleDelete={handleConfirmDelete}
        title="Delete Blog"
        message="Are you sure you want to delete this blog? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <DeleteConfirmPopUp
        isConfirmDeleteOpen={isConfirmPublishOpen}
        setIsConfirmDeleteOpen={setIsConfirmPublishOpen}
        handleDelete={handleConfirmPublish}
        title="Publish Blog"
        message="Are you sure you want to publish this blog?"
        confirmLabel="Publish"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default BlogPage;
